import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "../services/firebase";
import { Artwork } from "../types/artwork";
import ArtworkForm from "../components/ArtworkForm";
import OperationHistoryComponent from "../components/OperationHistory";
import { getStatusColor, getStatusText } from "../utils";
import "./EditArtwork.css";

const EditArtwork: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [artwork, setArtwork] = useState<Artwork | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArtwork = async () => {
      if (!id) {
        setError("Invalid artwork ID");
        setLoading(false);
        return;
      }

      try {
        const artworkRef = doc(firestore, "artworks", id);
        const artworkSnap = await getDoc(artworkRef);

        if (artworkSnap.exists()) {
          const data = artworkSnap.data();
          setArtwork({
            id: artworkSnap.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            isDigitized: data.isDigitized || false,
          } as Artwork);
        } else {
          setError("Artwork not found");
        }
      } catch (error) {
        console.error("Error fetching artwork:", error);
        setError("Error loading artwork");
      } finally {
        setLoading(false);
      }
    };

    fetchArtwork();
  }, [id]);

  const handleSubmit = (updatedArtwork: Artwork) => {
    navigate(`/artwork/${id}`);
  };

  if (loading) {
    return (
      <div className="edit-artwork-loading">
        <div className="edit-artwork-loading-spinner"></div>
        <p className="edit-artwork-loading-text">Loading artwork...</p>
      </div>
    );
  }

  if (error || !artwork || !id) {
    return (
      <div className="edit-artwork-error">
        <div className="edit-artwork-error-content">
          <div className="edit-artwork-error-icon">❌</div>
          <h1 className="edit-artwork-error-title">
            {error || "Artwork Not Found"}
          </h1>
          <p className="edit-artwork-error-description">
            The artwork you're trying to edit doesn't exist or has been removed.
          </p>
          <Link to="/artworks" className="edit-artwork-error-button">
            ← Back to Artworks
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-artwork-container">
      {/* Header */}
      <div className="edit-artwork-header">
        {/* Decorative elements */}
        <div className="edit-artwork-header-decoration-1"></div>
        <div className="edit-artwork-header-decoration-2"></div>

        <div className="edit-artwork-header-content">
          <div className="edit-artwork-header-left">
            <Link to={`/artwork/${id}`} className="edit-artwork-back-link">
              <span className="edit-artwork-back-arrow">←</span>
              Back to Artwork Details
            </Link>

            <div className="edit-artwork-title-section">
              <span className="edit-artwork-title-icon">✏️</span>
              <div className="edit-artwork-title-content">
                <h1 className="edit-artwork-title">Edit Artwork</h1>
                <p className="edit-artwork-subtitle">
                  {artwork.title} by {artwork.artist}
                </p>
              </div>
            </div>

            <div className="edit-artwork-header-badges">
              <div className="edit-artwork-header-badge">
                <span className="edit-artwork-header-badge-icon">🏛️</span>
                <span className="edit-artwork-header-badge-text">
                  {artwork.institution}
                </span>
              </div>

              <div className="edit-artwork-header-badge">
                <span
                  className="edit-artwork-status-badge"
                  style={{ backgroundColor: getStatusColor(artwork.status) }}
                >
                  <span>
                    {artwork.status === "pending" && "📋"}
                    {artwork.status === "in-progress" && "⚡"}
                    {artwork.status === "obtained" && "🎯"}
                    {artwork.isDigitized && "✅"}
                  </span>
                  {getStatusText(artwork.status)}
                </span>
              </div>

              <div className="edit-artwork-header-badge">
                <span
                  className={`edit-artwork-digitization-badge ${
                    artwork.isDigitized ? "digitized" : "not-digitized"
                  }`}
                >
                  <span className="edit-artwork-digitization-icon">
                    {artwork.isDigitized ? "🖼️" : "📸"}
                  </span>
                  {artwork.isDigitized ? "Digitized" : "Needs Photo"}
                </span>
              </div>
            </div>
          </div>

          <div className="edit-artwork-header-right">
            <Link to={`/artwork/${id}`} className="edit-artwork-view-button">
              <span className="edit-artwork-view-icon">👁️</span>
              View Details
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="edit-artwork-main-content">
        {/* Left Column - Edit Form */}
        <div>
          <div className="edit-artwork-form-section">
            <div className="edit-artwork-form-header">
              <span className="edit-artwork-form-icon">📝</span>
              <div className="edit-artwork-form-title-content">
                <h2 className="edit-artwork-form-title">
                  Edit Artwork Details
                </h2>
                <p className="edit-artwork-form-description">
                  Update the information for this artwork
                </p>
              </div>
            </div>

            <ArtworkForm artwork={artwork} onSubmit={handleSubmit} />
          </div>
        </div>

        {/* Right Column - Operation History */}
        <div>
          <OperationHistoryComponent artworkId={id} defaultShowForm={true} />
        </div>
      </div>

      {/* Tips Section */}
      <div className="edit-artwork-tips-section">
        <div className="edit-artwork-tips-header">
          <span className="edit-artwork-tips-icon">💡</span>
          <h3 className="edit-artwork-tips-title">Editing Tips</h3>
        </div>

        <div className="edit-artwork-tips-grid">
          {[
            {
              icon: "🎯",
              title: "Update Status",
              description:
                "Change the artwork status to reflect current progress",
            },
            {
              icon: "📸",
              title: "Digitization Status",
              description:
                "Mark as digitized if photos are available, or update when photography is completed",
            },
            {
              icon: "📞",
              title: "Contact Information",
              description:
                "Keep contact details updated for better communication",
            },
            {
              icon: "📝",
              title: "Add Notes",
              description: "Include any new information or observations",
            },
            {
              icon: "📋",
              title: "Track Operations",
              description: "Use the operation history to log interactions",
            },
            {
              icon: "🖼️",
              title: "Digital Assets",
              description:
                "Update digitization status when images are obtained or processed",
            },
          ].map((tip, index) => (
            <div key={index} className="edit-artwork-tip-card">
              <div className="edit-artwork-tip-header">
                <span className="edit-artwork-tip-icon">{tip.icon}</span>
                <h4 className="edit-artwork-tip-title">{tip.title}</h4>
              </div>
              <p className="edit-artwork-tip-description">{tip.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EditArtwork;
